import * as React from 'react';
import { useState, useRef, useEffect } from 'react';
import { FaPaperPlane, FaSpinner } from 'react-icons/fa';
import './ChatInput.scss';

interface ChatInputProps {
  onSendMessage: (message: string) => void | Promise<void>;
  disabled?: boolean;
  placeholder?: string;
  loading?: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  disabled = false,
  placeholder = "Type your message...",
  loading = false,
}) => {
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedMessage = message.trim();
    if (trimmedMessage && !disabled && !loading) {
      await onSendMessage(trimmedMessage);
      setMessage('');
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = async (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      await handleSubmit(e);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    // Auto-resize textarea
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  };

  // Focus input when not disabled
  useEffect(() => {
    if (!disabled && !loading && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [disabled, loading]);

  return (
    <form onSubmit={handleSubmit} className="chat-input">
      <div className="chat-input__container">
        <textarea
          ref={textareaRef}
          value={message}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled || loading}
          rows={1}
          className="chat-input__textarea"
          style={{ minHeight: '40px', maxHeight: '120px' }}
        />

        <button
          type="submit"
          disabled={!message.trim() || disabled || loading}
          className={`chat-input__button ${
            !message.trim() || disabled || loading
              ? 'chat-input__button--disabled'
              : loading
              ? 'chat-input__button--loading'
              : 'chat-input__button--enabled'
          }`}
        >
          {loading ? (
            <FaSpinner size={20} className="spin" />
          ) : (
            <FaPaperPlane size={20} />
          )}
        </button>
      </div>
    </form>
  );
};

export { ChatInput };
